import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { articleCommentsApi } from "../../Api/ArticleComments";
import { CommentContentType } from "../../Api/CommentsAPI";

import {
    videoCommentsApi,
    getVideoCommentErrorCode,
    getVideoCommentErrorStatus,
    isVideoCommentRequestCancelled,
} from "../../Api/VideoComments";
import ResolveVideoCommentReportDialog from "../../Components/UI/Comments/ResolveVideoCommentReportDialog";
import { RoutePaths } from "../../Constants/RoutePaths";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";
import {
    describeVideoCommentError,
    VideoCommentClientError,
} from "../../Hooks/Comments/Video/useVideoComments";
import {
    ResolveVideoCommentReportRequestDTO,
    VideoCommentModerationDecision,
    VideoCommentModerationReportDTO,
    VideoCommentReportStatus,
} from "../../Models/DTOs/VideoCommentsDTO";

const PAGE_SIZE = 20;
const STATUSES: Array<{ value: VideoCommentReportStatus; label: string }> = [
    { value: "open", label: "Open" },
    { value: "reviewed", label: "Reviewed" },
    { value: "dismissed", label: "Dismissed" },
    { value: "actioned", label: "Actioned" },
];

interface ResolutionSelection {
    report: VideoCommentModerationReportDTO;
    decision: VideoCommentModerationDecision;
}

const CommentModerationQueue: React.FC<{
    contentType: CommentContentType;
    onContentTypeChange: (type: CommentContentType) => void;
}> = ({ contentType, onContentTypeChange }) => {
    const api = contentType === "article" ? articleCommentsApi : videoCommentsApi;
    const auth = useContext(UserAuthenticationContext);
    const authRef = useRef(auth);
    authRef.current = auth;
    const mountedRef = useRef(true);
    const requestRef = useRef<AbortController | null>(null);
    const controllersRef = useRef<Set<AbortController>>(new Set());

    const [status, setStatus] = useState<VideoCommentReportStatus>("open");
    const [items, setItems] = useState<VideoCommentModerationReportDTO[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selection, setSelection] = useState<ResolutionSelection | null>(null);

    const withModeratorToken = useCallback(
        async <T,>(operation: (token: string) => Promise<T>): Promise<T> => {
            const currentAuth = authRef.current;
            let token = await currentAuth.getValidAccessToken();
            if (!token) throw new VideoCommentClientError("authentication_required");
            try {
                return await operation(token);
            } catch (requestError) {
                if (getVideoCommentErrorStatus(requestError) !== 401) throw requestError;
                const refreshed = await currentAuth.refreshTokens();
                token = refreshed ? await currentAuth.getValidAccessToken() : null;
                if (!token) throw requestError;
                return operation(token);
            }
        },
        []
    );

    const fetchReports = useCallback(
        async (
            selectedStatus: VideoCommentReportStatus,
            append: boolean,
            cursor: string | null = null
        ) => {
            if (!append) requestRef.current?.abort();
            const controller = new AbortController();
            controllersRef.current.add(controller);
            requestRef.current = controller;
            append ? setLoadingMore(true) : setLoading(true);
            if (!append) setError(null);

            try {
                const page = await withModeratorToken((token) =>
                    api.getModerationReports(
                        selectedStatus,
                        PAGE_SIZE,
                        cursor,
                        token,
                        controller
                    )
                );
                if (!mountedRef.current || controller.signal.aborted) return;
                setItems((current) => {
                    if (!append) return page.items;
                    const merged = [...current];
                    const indexes = new Map(
                        merged.map((report, index) => [report.id, index])
                    );
                    page.items.forEach((report) => {
                        const index = indexes.get(report.id);
                        if (index === undefined) merged.push(report);
                        else merged[index] = report;
                    });
                    return merged;
                });
                setNextCursor(page.next_cursor);
                setTotal(page.total);
                setError(null);
                return page;
            } catch (requestError) {
                if (
                    mountedRef.current &&
                    !controller.signal.aborted &&
                    !isVideoCommentRequestCancelled(requestError)
                ) {
                    setError(describeVideoCommentError(requestError));
                }
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                    setLoadingMore(false);
                }
                if (requestRef.current === controller) requestRef.current = null;
                controllersRef.current.delete(controller);
            }
        },
        [api, withModeratorToken]
    );

    const resolve = useCallback(
        async (
            report: VideoCommentModerationReportDTO,
            request: ResolveVideoCommentReportRequestDTO
        ) => {
            const controller = new AbortController();
            controllersRef.current.add(controller);
            try {
                const result = await withModeratorToken((token) =>
                    api.resolveReport(report.id, request, token, controller)
                );
                if (!mountedRef.current || controller.signal.aborted) return null;
                await fetchReports(status, false, null);
                return result;
            } catch (requestError) {
                if (!mountedRef.current || controller.signal.aborted) throw requestError;
                const errorCode = getVideoCommentErrorCode(requestError);
                if (errorCode === "comment_report_already_resolved") {
                    // Another moderator completed the decision while this dialog
                    // was open. Reconcile the queue and treat the desired terminal
                    // state as complete instead of trapping the moderator in a
                    // stale retry loop.
                    await fetchReports(status, false, null);
                    setSelection(null);
                    return null;
                }
                if (errorCode === "comment_version_conflict") {
                    const refreshedPage = await fetchReports(status, false, null);
                    const refreshedReport = refreshedPage?.items.find(
                        (item) => item.id === report.id
                    );
                    if (refreshedReport) {
                        setSelection({
                            report: refreshedReport,
                            decision: request.decision,
                        });
                    }
                }
                throw requestError;
            } finally {
                controllersRef.current.delete(controller);
            }
        },
        [api, fetchReports, status, withModeratorToken]
    );

    useEffect(() => {
        setItems([]);
        setNextCursor(null);
        setTotal(0);
        void fetchReports(status, false, null);
    }, [fetchReports, status]);

    useEffect(() => {
        mountedRef.current = true;
        const activeControllers = controllersRef.current;
        return () => {
            mountedRef.current = false;
            activeControllers.forEach((controller) => controller.abort());
            activeControllers.clear();
        };
    }, []);

    return (
        <section className="comment-moderation" aria-labelledby="comment-moderation-title">
            <div className="comment-moderation__heading">
                <div>
                    <h1 id="comment-moderation-title">Comment moderation</h1>
                    <p>
                        Review member reports. Queue pages are snapshot-bound; refresh
                        to see reports that arrived after the current traversal began.
                    </p>
                </div>
                <button
                    type="button"
                    className="comment-moderation__refresh"
                    disabled={loading}
                    onClick={() => void fetchReports(status, false, null)}
                >
                    <FiRefreshCw aria-hidden="true" />
                    <span>{loading ? "Refreshing…" : "Refresh queue"}</span>
                </button>
            </div>

            <div className="comment-moderation__filters" role="group" aria-label="Comment type">
                <button type="button" aria-pressed={contentType === "video"} onClick={() => onContentTypeChange("video")}>
                    Video comments
                </button>
                <button type="button" aria-pressed={contentType === "article"} onClick={() => onContentTypeChange("article")}>
                    Article comments
                </button>
            </div>

            <div className="comment-moderation__filters" aria-label="Report status">
                {STATUSES.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={status === option.value}
                        onClick={() => setStatus(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <p>
                {total.toLocaleString()} {status} {total === 1 ? "report" : "reports"}
            </p>

            {loading && <p role="status">Loading reports…</p>}
            {error && (
                <div className="video-comments__error" role="alert">
                    <p>{error}</p>
                    <button
                        type="button"
                        onClick={() => void fetchReports(status, false, null)}
                    >
                        Try again
                    </button>
                </div>
            )}

            {!loading && !error && items.length === 0 && (
                <p>There are no {status} reports.</p>
            )}

            <div className="comment-moderation__list">
                {items.map((report) => (
                    <article className="comment-moderation__report" key={report.id}>
                        <header>
                            <strong>Report #{report.id}</strong>
                            <time dateTime={report.created_at}>
                                {new Date(report.created_at).toLocaleString()}
                            </time>
                        </header>
                        <dl>
                            <dt>Status</dt>
                            <dd>{report.status}</dd>
                            <dt>Reason</dt>
                            <dd>{report.reason_code.replace(/_/g, " ")}</dd>
                            <dt>Reporter</dt>
                            <dd>
                                {report.reporter.display_name} ({report.reporter.user_id})
                            </dd>
                            <dt>{contentType === "article" ? "Article" : "Video"} / comment</dt>
                            <dd>
                                {contentType === "article" ? report.article_id : report.video_id} / {report.comment_id}
                            </dd>
                            {report.details && (
                                <>
                                    <dt>Details</dt>
                                    <dd>{report.details}</dd>
                                </>
                            )}
                            {report.resolution && (
                                <>
                                    <dt>Resolution</dt>
                                    <dd>{report.resolution}</dd>
                                </>
                            )}
                            {report.reviewed_at && (
                                <>
                                    <dt>Reviewed</dt>
                                    <dd>
                                        {new Date(report.reviewed_at).toLocaleString()}
                                        {report.reviewer
                                            ? ` by ${report.reviewer.display_name}`
                                            : ""}
                                    </dd>
                                </>
                            )}
                        </dl>

                        <div className="comment-moderation__comment">
                            <strong>
                                {report.comment.author?.display_name || "Deleted member"}
                            </strong>
                            <p>
                                {report.comment.is_deleted || report.comment.body === null
                                    ? "This comment was deleted."
                                    : report.comment.body}
                            </p>
                        </div>

                        <div className="comment-moderation__actions">
                            <Link to={contentType === "article"
                                ? (report.article_id ? RoutePaths.ReadArticle(report.article_id) : RoutePaths.BrowseArticles)
                                : (report.video_id ? RoutePaths.Watch(report.video_id) : RoutePaths.Browse)}>
                                Open {contentType}
                            </Link>
                            {report.status === "open" && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setSelection({ report, decision: "dismiss" })
                                        }
                                    >
                                        Dismiss report
                                    </button>
                                    <button
                                        type="button"
                                        className="is-danger"
                                        onClick={() =>
                                            setSelection({ report, decision: "delete" })
                                        }
                                    >
                                        Delete comment
                                    </button>
                                </>
                            )}
                        </div>
                    </article>
                ))}
            </div>

            {nextCursor && (
                <button
                    type="button"
                    className="video-comments__load-more"
                    disabled={loadingMore}
                    onClick={() => void fetchReports(status, true, nextCursor)}
                >
                    {loadingMore ? "Loading…" : "Load more reports"}
                </button>
            )}

            {selection && (
                <ResolveVideoCommentReportDialog
                    report={selection.report}
                    decision={selection.decision}
                    onClose={() => setSelection(null)}
                    onResolve={(request) => resolve(selection.report, request)}
                />
            )}
        </section>
    );
};

const CommentModeration: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const contentType: CommentContentType = searchParams.get("type") === "article" ? "article" : "video";
    return <CommentModerationQueue
        key={contentType}
        contentType={contentType}
        onContentTypeChange={(type) => setSearchParams(type === "article" ? { type } : {})}
    />;
};

export default CommentModeration;
